import { useEffect } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navbar } from './components/Navbar';
import { Hero3D } from './components/Hero3D';
import { TrustStats } from './components/TrustStats';
import { Percorsi } from './components/Percorsi';
import { ValueProps } from './components/ValueProps';
import { KPI } from './components/KPI';
import { FAQ } from './components/FAQ';
import { FinalCTA } from './components/FinalCTA';
import { Footer } from './components/Footer';
import { StickyCTA } from './components/StickyCTA';
import { useScrollAnimations } from './hooks/useScrollAnimations';
import { ConsentBanner } from './components/ConsentBanner';
import { ContentProvider } from './contexts/ContentContext';
import { MediaProvider } from './contexts/MediaContext';
import {
  getOrganizationSchema,
  getProductSchema,
  getFAQSchema,
} from './utils/seo';

function App() {
  const { t, i18n } = useTranslation();
  useScrollAnimations();

  // Generate FAQ schema from translations
  const faqItems = t('faq.items', { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }>;

  useEffect(() => {
    // Set HTML lang attribute
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <HelmetProvider>
      <Helmet>
        <title>{t('seo.title')}</title>
        <meta
          name="description"
          content={t('seo.description')}
        />
        <meta name="keywords" content={t('seo.keywords')} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t('seo.ogTitle')} />
        <meta
          property="og:description"
          content={t('seo.ogDescription')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://regai.it" />
        <meta property="og:image" content="https://regai.it/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('seo.twitterTitle')} />
        <meta
          name="twitter:description"
          content={t('seo.twitterDescription')}
        />
        <meta name="twitter:image" content="https://regai.it/twitter-image.jpg" />
        
        {/* Canonical */}
        <link rel="canonical" href="https://regai.it" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(getOrganizationSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(getProductSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(getFAQSchema(faqItems))}
        </script>
      </Helmet>

      <MediaProvider>
        <ContentProvider>
          <div className="min-h-screen">
          {/* Skip to main content link for accessibility */}
          <a href="#main-content" className="skip-link">
            {t('seo.skipLink')}
          </a>

            <Navbar />

            <main id="main-content">
              <Hero3D />
              <TrustStats />
              <Percorsi />
              <ValueProps />
              <KPI />
              <FAQ />
              <FinalCTA />
            </main>

            <Footer />
            <StickyCTA />
            <ConsentBanner />
          </div>
        </ContentProvider>
      </MediaProvider>
    </HelmetProvider>
  );
}

export default App;
